import { observable, action, makeObservable, computed } from "mobx";
import axios from "axios";
import { serverUrl } from ".";
import { md, sm } from "../constant";
import emailjs from "@emailjs/browser";
import notificationStore from "./notificationStore";

export type serviceType =
  | "3d_printing"
  | "3d_scanning"
  | "3d_modeling"
  | "product_development"
  | "patent_drawings"
  | "low_vol_production";

export const serviceTypes: serviceType[] = [
  "3d_printing",
  "3d_scanning",
  "3d_modeling",
  "product_development",
  "patent_drawings",
  "low_vol_production",
];

export const serviceNames: string[] = [
  "3D Printing",
  "3D Scanning",
  "3D Modeling",
  "Product Development",
  "Patent Drawings",
  "Low Vol Production",
];

export interface IformField {
  id: string;
  name?: string;
  placeholder?: string;
  type?: string;
  options?: string[];
}

export class QuotesStore {
  screenWidth: number = 1024;
  fields: IformField[] = [];
  files: any[] = [];
  isSending: boolean = false;

  constructor() {
    makeObservable(this, {
      screenWidth: observable,
      files: observable,
      isSending: observable,
      sendQuoteRequest: action,
      getFormFields: action,
      setFile: action,
      isMd: computed,
      isSm: computed,
    });
  }

  get isMd(): boolean {
    return this.screenWidth <= md && this.screenWidth > sm;
  }
  get isSm(): boolean {
    return this.screenWidth <= sm;
  }

  getFormFields = async (service: string) => {
    try {
      const response = await axios.get(
        `${serverUrl}/form_manager/fields?service=${service}`
      );
      this.fields = response.data || [];
    } catch (err) {
      console.error("quotesStore.ts => getFormFields:", err);
    }
  };

  setFile = (file: any) => {
    this.files.push(file);
  };

  setScreenWidth = (width: number) => {
    this.screenWidth = width;
  };

  /**
   * Saves the quote request, then attempts a confirmation email to the
   * customer. Resolves true when the request was saved.
   *
   * The email is best-effort. A failure there must not be reported as the
   * request failing: by that point it is already in the database. When
   * EmailJS's Gmail grant expired, every customer was told their quote had
   * failed when it had in fact been received.
   */
  sendQuoteRequest = async (values: any): Promise<boolean> => {
    this.isSending = true;
    values.files = this.files;
    try {
      const response = await axios.post(`${serverUrl}/quote_requests`, values);
      if (response.data?.message !== "quote_request_added") {
        console.error("quotesStore => sendQuoteRequest: unexpected response", response.data);
        this.notifyFailure();
        return false;
      }

      notificationStore.addNotification({
        message: "Quote Request sent successfully!",
        description: "Your quote request has been sent successfully!",
      });
      // Otherwise a second submission from the same page re-attaches these.
      this.files = [];

      try {
        await emailjs.send(
          "service_in0wtt5",
          "template_hn69ioe",
          {
            email: values.email,
          },
          "R-YmZ0e-1jlhkC77g"
        );
      } catch (error) {
        // The request is saved; only the courtesy confirmation did not go out.
        console.warn("quotesStore => sendQuoteRequest: confirmation email not sent", error);
      }

      return true;
    } catch (err) {
      console.error("quotesStore => sendQuoteRequest:", err);
      this.notifyFailure();
      return false;
    } finally {
      this.isSending = false;
    }
  };

  private notifyFailure = () => {
    notificationStore.addNotification({
      message: "Quote Request failed!",
      description:
        "We couldn't send your request. Please try again, or email Info@Wellstronics3D.com.",
    });
  };

  trackSubmitForm = async ({ sessionId, service }: { sessionId: string; service: string }) => {
    try {
      await axios.post(`${serverUrl}/metrics/track-submit-form`, { sessionId, service });
    } catch (err) {
      console.error("quotesStore.ts => trackSubmitForm:", err);
    }
  };
}


const quotesStore = new QuotesStore();

export default quotesStore;
